import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, InputNumber, Modal } from '@maxtropy/components';
import { useEffect, useState } from 'react';
import styles from './index.module.scss';
import { ThermalPowerConfig } from './types';

interface ConfigThermalPowerModalProps {
  open: boolean;
  values: ThermalPowerConfig;
  onCancel: () => void;
  onOk: (values: ThermalPowerConfig) => void;
}

const tooltipIcon = <InfoCircleOutlined style={{ color: 'var(--mx-warning-color)' }} />;

const ConfigThermalPowerModal = (props: ConfigThermalPowerModalProps) => {
  const { open, values, onCancel, onOk } = props;
  const [form] = Form.useForm<ThermalPowerConfig>();
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    if (open) {
      form.setFieldsValue(values);
    }
  }, [form, open, values]);

  const handleOk = async () => {
    setConfirmLoading(true);
    const formValues = await form
      .validateFields()
      .then(res => res)
      .catch(() => null);
    setConfirmLoading(false);

    if (!formValues) {
      return;
    }

    onOk(formValues);
  };

  const validateRangeMax = (minField: keyof ThermalPowerConfig, message: string) => (_: unknown, value?: number) => {
    const minValue = form.getFieldValue(minField);

    if (value === undefined || minValue === undefined || value > minValue) {
      return Promise.resolve();
    }

    return Promise.reject(new Error(message));
  };

  return (
    <Modal
      title="配置"
      open={open}
      size="normal"
      contentClassName="modal-form-content"
      destroyOnClose
      confirmLoading={confirmLoading}
      onCancel={onCancel}
      onOk={handleOk}
    >
      <Form form={form} layout="horizontal" className={styles.configForm} wrapperCol={{ flex: 1 }}>
        <Form.Item
          label="出力分割线"
          name="powerSplitLine"
          tooltip={{
            title: '根据单台发电机组的发电功率的分隔线，划分出力区间',
            icon: tooltipIcon,
          }}
          rules={[{ required: true, message: '请输入' }]}
        >
          <InputNumber placeholder="请输入" addonAfter="MW" style={{ width: '100%' }} min={0} />
        </Form.Item>
        <div className={styles.modalGroupTitle}>单台发电机组速率</div>
        <Form.Item
          label="低于出力分隔线"
          required
          tooltip={{
            title: '低于分隔线单台发电机组每分钟可调节量（上升/下降）',
            icon: tooltipIcon,
          }}
        >
          <div className={styles.rangeInputGroup}>
            <Form.Item noStyle name="belowMinAdjustRate" rules={[{ required: true, message: '请输入' }]}>
              <InputNumber placeholder="请输入" style={{ width: '100%' }} min={0} />
            </Form.Item>
            <span className={styles.rangeSeparator}>~</span>
            <Form.Item
              noStyle
              name="belowMaxAdjustRate"
              dependencies={['belowMinAdjustRate']}
              rules={[
                { required: true, message: '请输入' },
                { validator: validateRangeMax('belowMinAdjustRate', '上限需大于下限') },
              ]}
            >
              <InputNumber placeholder="请输入" style={{ width: '100%' }} min={0} />
            </Form.Item>
            <span className={styles.rangeUnit}>MW/min</span>
          </div>
        </Form.Item>
        <Form.Item
          label="高于出力分隔线"
          required
          tooltip={{
            title: '高于分隔线单台发电机组每分钟可调节量（上升/下降）',
            icon: tooltipIcon,
          }}
        >
          <div className={styles.rangeInputGroup}>
            <Form.Item noStyle name="aboveMinAdjustRate" rules={[{ required: true, message: '请输入' }]}>
              <InputNumber placeholder="请输入" style={{ width: '100%' }} min={0} />
            </Form.Item>
            <span className={styles.rangeSeparator}>~</span>
            <Form.Item
              noStyle
              name="aboveMaxAdjustRate"
              dependencies={['aboveMinAdjustRate']}
              rules={[
                { required: true, message: '请输入' },
                { validator: validateRangeMax('aboveMinAdjustRate', '上限需大于下限') },
              ]}
            >
              <InputNumber placeholder="请输入" style={{ width: '100%' }} min={0} />
            </Form.Item>
            <span className={styles.rangeUnit}>MW/min</span>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ConfigThermalPowerModal;
